import { useEffect, useRef, useState } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { courseService } from "../../services/courseService";
import { Checkbox } from "../ui/Checkbox";
import { debounce } from "../../utils/debounce";

interface CourseLite {
  _id: string;
  title: string;
}

export interface GlobalChangesCourseFilterValue {
  allCourses: boolean;
  courseIds: string[];
}

interface GlobalChangesCourseFilterProps {
  value: GlobalChangesCourseFilterValue;
  onChange: (next: GlobalChangesCourseFilterValue) => void;
  disabled?: boolean;
}

export function GlobalChangesCourseFilter({
  value,
  onChange,
  disabled = false,
}: GlobalChangesCourseFilterProps) {
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [searchResults, setSearchResults] = useState<CourseLite[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Local map of selected course IDs → the lightweight course payload we
  // need to render the chips. Entries are populated when the admin picks a
  // course from the dropdown.
  const [selectedCourses, setSelectedCourses] = useState<
    Record<string, CourseLite>
  >({});

  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch the catalog total once on mount. The backend returns a
  // `count` (the same number it would return in `pagination.totalCourses`),
  // which is cheaper than pulling the full course list.
  useEffect(() => {
    let isMounted = true;
    const fetchTotal = async () => {
      try {
        const response = await courseService.getAllCourses({ limit: 1 });
        // Defensive: support both `{ total }` and `{ count }` (this codebase's
        // convention) and the backend's `pagination.totalCourses`.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const r = response as any;
        const fromTopLevel =
          typeof r.total === "number"
            ? r.total
            : typeof r.count === "number"
              ? r.count
              : null;
        if (isMounted && fromTopLevel !== null) {
          setTotalCount(fromTopLevel);
          return;
        }
        // Fallback: pull a large page and count the rows. Catalog is bounded
        // so this is safe; if the catalog grows past 1000 we'll just show a
        // lower-bound total in the UI.
        const fallback = await courseService.getAllCourses({ limit: 1000 });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const courses = (fallback as any).data?.courses ?? [];
        if (isMounted) {
          setTotalCount(Array.isArray(courses) ? courses.length : 0);
        }
      } catch (err) {
        console.error("Failed to load course catalog total:", err);
        if (isMounted) {
          setTotalCount(0);
        }
      }
    };
    fetchTotal();
    return () => {
      isMounted = false;
    };
  }, []);

  // Debounced search, matching CourseSelector's 300ms cadence.
  const debouncedSearch = useRef(
    debounce(async (term: string) => {
      setIsSearching(true);
      try {
        const response = await courseService.getAllCourses({
          search: term,
          limit: 50,
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const list = (response as any).data?.courses ?? [];
        setSearchResults(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Course search failed:", err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
  );

  useEffect(() => {
    if (isSearchOpen) {
      debouncedSearch.current(searchTerm);
    }
  }, [isSearchOpen, searchTerm]);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!isSearchOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSearchOpen]);

  const handleToggleAll = (next: boolean) => {
    onChange({ allCourses: next, courseIds: [] });
  };

  const handleAddCourse = (course: CourseLite) => {
    if (value.allCourses) return;
    if (value.courseIds.includes(course._id)) return;
    onChange({
      allCourses: false,
      courseIds: [...value.courseIds, course._id],
    });
    setSelectedCourses((prev) => ({ ...prev, [course._id]: course }));
    setSearchTerm("");
  };

  const handleRemoveCourse = (courseId: string) => {
    onChange({
      allCourses: false,
      courseIds: value.courseIds.filter((id) => id !== courseId),
    });
    setSelectedCourses((prev) => {
      const next = { ...prev };
      delete next[courseId];
      return next;
    });
  };

  const selectedChips = value.courseIds
    .map((id) => selectedCourses[id])
    .filter((c): c is CourseLite => Boolean(c));

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
        <Checkbox
          checked={value.allCourses}
          disabled={disabled}
          onChange={(e) => handleToggleAll(e.target.checked)}
        />
        <span>Apply to all courses</span>
      </label>

      {value.allCourses ? (
        <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          All courses
          {totalCount !== null && (
            <span className="text-gray-500">
              {" "}
              ({totalCount.toLocaleString()} total)
            </span>
          )}
        </div>
      ) : (
        <div className="space-y-2" ref={containerRef}>
          <div className="relative">
            <button
              type="button"
              disabled={disabled}
              onClick={() => setIsSearchOpen((prev) => !prev)}
              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between text-sm bg-white hover:border-gray-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="text-gray-600">
                {value.courseIds.length > 0
                  ? `${value.courseIds.length} course${value.courseIds.length > 1 ? "s" : ""} selected`
                  : "Search and select courses"}
              </span>
              <Search size={16} className="text-gray-400" />
            </button>

            {isSearchOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                <div className="p-2 border-b border-gray-100 flex items-center gap-2 bg-gray-50">
                  <Search size={14} className="text-gray-400" />
                  <input
                    type="text"
                    className="w-full bg-transparent border-none outline-none text-sm placeholder:text-gray-400"
                    placeholder="Search courses..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  {isSearching && (
                    <Loader2
                      size={14}
                      className="animate-spin text-gray-400"
                    />
                  )}
                </div>

                <div className="max-h-60 overflow-y-auto">
                  {isSearching && searchResults.length === 0 ? (
                    <div className="px-4 py-8 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Loading courses...
                    </div>
                  ) : searchResults.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      No courses found
                    </div>
                  ) : (
                    searchResults.map((course) => {
                      const isSelected = value.courseIds.includes(course._id);
                      return (
                        <div
                          key={course._id}
                          className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
                            isSelected
                              ? "bg-indigo-50 text-indigo-700 font-medium"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                          onClick={() => {
                            if (!isSelected) handleAddCourse(course);
                          }}
                        >
                          <span className="truncate block">{course.title}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {selectedChips.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedChips.map((course) => (
                <div
                  key={course._id}
                  className="flex items-center gap-1 bg-indigo-50 text-indigo-700 px-2 py-1 rounded text-xs"
                >
                  <span className="max-w-[240px] truncate">{course.title}</span>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => handleRemoveCourse(course._id)}
                    className="hover:text-indigo-900 disabled:opacity-50"
                    aria-label={`Remove ${course.title}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default GlobalChangesCourseFilter;